import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "firebase/analytics";
import { getAnalytics } from "firebase/analytics";
import { ReactElement, StrictMode, useCallback } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  ScrollRestoration,
} from "react-router";
import type { Location } from "react-router";
import { twMerge } from "tailwind-merge";
import styles from "./App.module.css";
import { AppContent } from "./components/app/AppContent";
import { AppLifecycle } from "./components/app/AppLifecycle";
import { HiddenPlatformBanner } from "./components/app/HiddenPlatformBanner";
import { MenuDrawer } from "./components/menu/MenuDrawer";
import { MenuDrawerContextProvider } from "./components/menu/MenuDrawerContextProvider";
import { useMenuDrawerContext } from "./components/menu/MenuDrawerContextProvider.shared";
import { BusinessSwitcherDialog } from "./components/menu/dialogs/BusinessSwitcherDialog";
import { PushNotifications } from "./components/pushNotification/PushNotifications";
import { RefreshPwaBanner } from "./components/refreshPwa/RefreshPwaBanner";
import { Sidebar } from "./components/sidebar/Sidebar";
import { ToastProvider } from "./components/toast/useToast";
import { firebaseApp } from "./firebaseApp";
import {
  BasketProvider,
  SellarContextProvider,
  SellarSessionProvider,
  useSellarContext,
} from "./hooks";
import { SellarTrpcProvider } from "./hooks/api/v2/SellarTrpcProvider";
import { SellarIntercomProvider } from "./hooks/sellarIntercomProvider";
import { FirebaseRemoteConfigProvider } from "./hooks/useFirebaseRemoteConfig";
import { LocationHistoryProvider } from "./hooks/useLocationHistory";
import { useSellarCurrentBusiness } from "./hooks/useSellarCurrentBusiness.shared";
import { SignupContextProvider } from "./hooks/useSignupContext";
import { useSignupContext } from "./hooks/useSignupContext.shared";
import { getScrollRestorationStateKey } from "./utils/scrollRestoration";
import "./tailwind.css";
import { StorefrontSignupDialog } from "./pages/stockList/categoryPage/Storefront/StorefrontDialogs/StorefrontSignupDialog/StorefrontSignupDialog";
import { ParentNavigationProvider } from "./components/navigation/ParentNavigationProvider";
import { CourierHaulierSwitchPrompt } from "./components/courierHaulierSwitchPrompt/CourierHaulierSwitchPrompt";
import { AppChromeProvider } from "./components/app/AppChromeContext";
import { useAppChrome } from "./components/app/useAppChrome";

const queryClient = new QueryClient();

if (import.meta.env.VITE_ENV === "production") getAnalytics(firebaseApp);

function App() {
  const [{ sidePanelSticky, sidePanelActive }] = useSellarContext();
  const { isMobileDisplayType, isMobileViewport, displayTypeOverride } =
    useMenuDrawerContext();
  const { signupDialogActive } = useSignupContext();
  const { business } = useSellarCurrentBusiness();
  const { mode: appChromeMode } = useAppChrome();
  const showOperationalChrome = appChromeMode === "operational";
  const showApplicationServices = appChromeMode !== "focused";
  // Desktop: lock shell to the dynamic viewport so the PWA banner spacer is
  // taken out of flex remaining space (no h-dvh stacking / overscroll).
  // Mobile: keep min-h-dvh document scroll for native iOS scrolling.
  const lockViewportShell = !isMobileViewport;
  const getScrollRestorationKey = useCallback(
    (location: Location) => {
      const stateKey = getScrollRestorationStateKey(location.state);
      return `${business?.id ?? "no-business"}:${
        stateKey ?? `${location.pathname}${location.search}`
      }`;
    },
    [business?.id],
  );

  return (
    <ParentNavigationProvider>
      <AppLifecycle />
      <div
        className={twMerge(
          "flex flex-col",
          lockViewportShell ? "h-dvh overflow-hidden" : "min-h-dvh",
        )}
      >
        <RefreshPwaBanner />
        {showOperationalChrome && business?.Supplier?.isHidden && (
          <HiddenPlatformBanner isMobile={isMobileViewport} />
        )}
        <div
          style={{
            transition: "margin 0.2s ease-out",
          }}
          className={twMerge(
            showOperationalChrome &&
              displayTypeOverride !== "hidden" &&
              sidePanelSticky &&
              sidePanelActive &&
              styles.sidePanelMargin,
            "grid min-h-0 min-w-0 flex-1 grid-rows-[[stack]_1fr]",
            showOperationalChrome
              ? "grid-cols-[min-content_[stack]_1fr]"
              : "grid-cols-1",
            lockViewportShell && "overflow-hidden",
            showOperationalChrome &&
              isMobileDisplayType &&
              "*:col-[stack/stack] *:row-[stack/stack]",
          )}
        >
          {showOperationalChrome && (
            <MenuDrawer>
              <Sidebar />
            </MenuDrawer>
          )}
          {showApplicationServices && <BusinessSwitcherDialog />}

          <ToastProvider>
            <AppContent />
            {showApplicationServices && <PushNotifications />}
          </ToastProvider>
        </div>
      </div>
      {showApplicationServices && (
        <StorefrontSignupDialog active={signupDialogActive} />
      )}
      {showApplicationServices && <CourierHaulierSwitchPrompt />}
      <ScrollRestoration getKey={getScrollRestorationKey} />
    </ParentNavigationProvider>
  );
}

function AppProviders(): ReactElement {
  return (
    <SellarTrpcProvider queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <SignupContextProvider>
          <SellarContextProvider>
            <SellarIntercomProvider>
              <SellarSessionProvider>
                <FirebaseRemoteConfigProvider>
                  <MenuDrawerContextProvider>
                    <BasketProvider>
                      <StrictMode>
                        <LocationHistoryProvider>
                          <AppChromeProvider>
                            <App />
                          </AppChromeProvider>
                        </LocationHistoryProvider>
                      </StrictMode>
                    </BasketProvider>
                  </MenuDrawerContextProvider>
                </FirebaseRemoteConfigProvider>
              </SellarSessionProvider>
            </SellarIntercomProvider>
          </SellarContextProvider>
        </SignupContextProvider>

        <ReactQueryDevtools initialIsOpen={true} position={"bottom"} />
      </QueryClientProvider>
    </SellarTrpcProvider>
  );
}

const router = createBrowserRouter([
  {
    path: "*",
    element: <AppProviders />,
  },
]);

function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}

export default AppRouter;
